import { FC, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { trackEvent } from "~/utils/analytics";

type Props = {
  isOpen: boolean;
  duplicateFiles: string[];
  pendingFiles: File[];
  onConfirm: () => void;
  onContinueWithoutDuplicates: () => void;
  onCancel: () => void;
};

export const DuplicateFilesModal: FC<Props> = ({
  isOpen,
  duplicateFiles,
  pendingFiles,
  onContinueWithoutDuplicates,
  onCancel,
}) => {
  const allFilesDuplicate = duplicateFiles?.length === pendingFiles?.length;

  useEffect(() => {
    if (isOpen && duplicateFiles.length > 0) {
      trackEvent({
        eventName: "app/case/duplicate_modal.modal/shown",
        properties: {
          duplicateCount: duplicateFiles.length,
          totalFileCount: pendingFiles.length,
          duplicateFilenames: duplicateFiles,
        },
      });
    }
  }, [isOpen, duplicateFiles, pendingFiles]);

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="documentsList.duplicateFiles.titleDetected"
              defaultMessage="Duplicate Detected"
            />
          </DialogTitle>
        </DialogHeader>
        <div>
          <p className="mb-2">
            <FormattedMessage
              id="documentsList.duplicateFiles.existingFiles"
              defaultMessage="This already exists:"
            />
          </p>
          <ul className="list-disc pl-6 max-h-40 overflow-y-auto">
            {duplicateFiles.map((filename) => (
              <li key={filename} className="text-sm text-gray-600">
                {filename}
              </li>
            ))}
          </ul>
          {!allFilesDuplicate && (
            <p className="mt-2 text-sm">
              <FormattedMessage
                id="documentsList.duplicateFiles.skipMessage"
                defaultMessage="Existing files will be skipped."
              />
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            onClick={() => {
              trackEvent({
                eventName: "app/case/duplicate_modal/cancel.button/click",
                properties: {
                  duplicateCount: duplicateFiles.length,
                  totalFileCount: pendingFiles.length,
                },
              });
              onCancel();
            }}
            variant={allFilesDuplicate ? "default" : "ghost"}
          >
            {allFilesDuplicate ? (
              <FormattedMessage id="common.close" defaultMessage="Close" />
            ) : (
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            )}
          </Button>
          {!allFilesDuplicate && (
            <Button
              onClick={() => {
                trackEvent({
                  eventName:
                    "app/case/duplicate_modal/continue_without.button/click",
                  properties: {
                    duplicateCount: duplicateFiles.length,
                    uploadedCount: pendingFiles.length - duplicateFiles.length,
                  },
                });
                onContinueWithoutDuplicates();
              }}
            >
              <FormattedMessage id="continue" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
